import { isIOS, isPhone } from '@helpers';
import { width as deviceWidth } from 'react-native-device-detection';

// ---------------------------------------------------------------------------
// Layout primitives shared across the auth screens (TablerWrapper + consumers).
// Numbers here mirror the values that previously lived inline in the wrapper
// and its consumers — preserved exactly so that existing screens don't shift
// pixel positions. Names give them meaning; if any of these later turn out to
// be no-op crutches from the old layout, they can be reduced/removed safely.
// ---------------------------------------------------------------------------

// Card width on web (the bordered "auth card" surface centered in the window).
export const AUTH_WEB_CARD_WIDTH = 480;

// Outer mainWrapper width on tablet (mobile path falls back to '100%').
export const AUTH_TABLET_WRAPPER_WIDTH = 382;

// Horizontal padding inside mainWrapper on phones.
export const AUTH_PHONE_HORIZONTAL_PADDING = 29;

// Bottom padding the centerBlock reserves on phones to keep content off the
// bottom edge.
export const AUTH_PHONE_BOTTOM_PADDING = 20;

// Extra top margin applied on tall phones (window height > the threshold).
// Originated as a heuristic in the old wrapper.
export const AUTH_TALL_PHONE_TOP_MARGIN = 20;
export const AUTH_TALL_PHONE_HEIGHT_THRESHOLD = 700;

// Top padding inside mainWrapper, gives content breathing room from the top
// of the wrapper.
export const AUTH_WRAPPER_TOP_PADDING = 30;

// Web-only negative margin that biases the auth card slightly above center.
export const AUTH_WEB_CARD_TOP_BIAS = -60;

// Web card chrome.
export const AUTH_WEB_CARD_PADDING_HORIZONTAL = 50;
export const AUTH_WEB_CARD_PADDING_VERTICAL = 29;

// ---------------------------------------------------------------------------
// Per-screen TablerWrapper offsets.
//
// These came from the old layout where mainWrapper was pinned to a fixed
// height and the offsets were used to nudge content into place. They are
// kept here, named, so the call sites stop reading like noise. Values match
// the originals exactly to preserve UI; the platform/device tiers below
// reproduce the previous `isIOS ? (width > 1080 ? A : B) : C` patterns.
// ---------------------------------------------------------------------------

const isLargeIOS = isIOS && deviceWidth > 1080;

/** Used by createAccount, factoryLanguage, factoryTime. */
export const AUTH_OFFSET_FORM_LARGE = isIOS ? (isLargeIOS ? -103 : -105) : -180;

/** Used by enterEmail. */
export const AUTH_OFFSET_FORM_SMALL = isIOS ? (isLargeIOS ? -50 : -39) : -22;

/** Used by connectorEmail and downloadConnector. */
export const AUTH_OFFSET_CONNECTOR = isIOS ? (isLargeIOS ? 0 : -5) : -16;

/** Used by createPassword and forgotPass. */
export const AUTH_OFFSET_BACK_FORM = -27;

/** Used by confirmCode. */
export const AUTH_OFFSET_CONFIRM_CODE = -62;

/** Used by manualLogin. */
export const AUTH_OFFSET_MANUAL_LOGIN = -40;

/** Used by welcome. */
export const AUTH_OFFSET_WELCOME = -47;

/** Used by enterName. */
export const AUTH_OFFSET_ENTER_NAME = -75;

// ---------------------------------------------------------------------------
// Logo offsets shared across screens. Keeps the same negative-margin nudges
// that were inlined per-screen, but names them and dedupes the duplicates.
// ---------------------------------------------------------------------------

/** Logo marginTop for the standard "small + isBack" auth header. */
export const AUTH_LOGO_MARGIN_TOP_SMALL = -20;

/** Logo marginTop for enterEmail (single-input variant). */
export const AUTH_LOGO_MARGIN_TOP_SINGLE_INPUT = -18;

/** Logo marginTop for welcome screen. */
export const AUTH_LOGO_MARGIN_TOP_WELCOME = -25;

/** Logo marginTop for connectorEmail / downloadConnector (with email subtitle). */
export const AUTH_LOGO_MARGIN_TOP_CONNECTOR = isIOS ? (isLargeIOS ? -13 : -25) : -20;

/** Logo maxWidth on tablet vs phone for the standard form header. */
export const AUTH_LOGO_MAX_WIDTH_TABLET = 350;
export const AUTH_LOGO_MAX_WIDTH_PHONE = 300;
export const AUTH_LOGO_MAX_WIDTH = isPhone ? AUTH_LOGO_MAX_WIDTH_PHONE : AUTH_LOGO_MAX_WIDTH_TABLET;

/** Logo offset prop used by the standard form header (createAccount, etc.). */
export const AUTH_LOGO_OFFSET_FORM = 29;

/** Logo offset prop used by connector screens. */
export const AUTH_LOGO_OFFSET_CONNECTOR = 28;
